import { computed, Injectable, signal, WritableSignal } from '@angular/core';

import { Position } from '@connect-services/client/services/map-elements-position.service';

export type ModalInstanceState = {
    hasBeenMoved: boolean;
    isOpen: boolean;
    position: Position;
};

export type ModalState = Record<MapModal, ModalInstanceState>;

export enum MapModal {
    EDITION = 'edition',
    FILTER = 'filter',
    MEASUREMENT = 'measurement',
    NONE = 'none',
    SELECTION = 'selection',
    SHAPING = 'shaping',
    SNAP = 'snap',
}

export type OpenedModalElement = {
    element: HTMLElement;
    type: MapModal;
};

export const DEFAULT_MODAL_POSITION: Position = { x: 0, y: 0 };

@Injectable({
    providedIn: 'root',
})
export class MapModalService {
    public readonly areModalsHidden = signal(false);
    public readonly hasAnyModalOpen = computed((): boolean => {
        return !!this.getOpenedModalTypes().length;
    });
    public readonly state: WritableSignal<ModalState> = signal(MapModalService.createInitialState());

    private readonly autoMoving = signal<boolean>(false);
    private readonly modalElements = new Map<MapModal, HTMLElement>();
    private readonly modalOnClose = new Map<MapModal, (...args: unknown[]) => void>();

    private static createDefaultModalState(): ModalInstanceState {
        return {
            hasBeenMoved: false,
            isOpen: false,
            position: { ...DEFAULT_MODAL_POSITION },
        };
    }

    private static createInitialState(): ModalState {
        return {
            [MapModal.EDITION]: MapModalService.createDefaultModalState(),
            [MapModal.FILTER]: MapModalService.createDefaultModalState(),
            [MapModal.MEASUREMENT]: MapModalService.createDefaultModalState(),
            [MapModal.NONE]: MapModalService.createDefaultModalState(),
            [MapModal.SELECTION]: MapModalService.createDefaultModalState(),
            [MapModal.SHAPING]: MapModalService.createDefaultModalState(),
            [MapModal.SNAP]: MapModalService.createDefaultModalState(),
        };
    }

    public close<TArgs extends unknown[] = []>(type: MapModal, ...args: TArgs): void {
        this.state.update((current: ModalState) => ({
            ...current,
            [type]: {
                ...current[type],
                isOpen: false,
            },
        }));

        if (!this.modalOnClose.has(type)) {
            return;
        }

        const onClose = this.modalOnClose.get(type) as ((...args: TArgs) => void) | undefined;
        onClose?.(...args);
        this.modalOnClose.delete(type);
    }

    public getElement(type: MapModal): HTMLElement | null {
        return this.modalElements.get(type) ?? null;
    }

    public getOpenedElements(): OpenedModalElement[] {
        return this.getOpenedModalTypes().flatMap((type: MapModal) => {
            const element: HTMLElement | null = this.getElement(type);
            if (!element) {
                return [];
            }

            return [
                {
                    element,
                    type,
                },
            ];
        });
    }

    public isAutoMoving(): boolean {
        return this.autoMoving();
    }

    public markAsMoved(type: MapModal): void {
        this.state.update((current: ModalState) => ({
            ...current,
            [type]: {
                ...current[type],
                hasBeenMoved: true,
            },
        }));
    }

    public moveTo(type: MapModal, position: Position): void {
        this.state.update((current: ModalState) => ({
            ...current,
            [type]: {
                ...current[type],
                position,
            },
        }));
    }

    public open<TArgs extends unknown[] = []>(
        type: MapModal,
        options: { onClose?: (...args: TArgs) => void } = {},
    ): void {
        const { onClose } = options;

        this.state.update((current: ModalState) => {
            if (!current[type]) {
                return current;
            }

            return {
                ...current,
                [type]: {
                    ...current[type],
                    hasBeenMoved: false,
                    isOpen: true,
                },
            };
        });

        if (!onClose) {
            this.modalOnClose.delete(type);

            return;
        }

        this.modalOnClose.set(type, onClose as (...args: unknown[]) => void);
    }

    public registerElement(type: MapModal, element: HTMLElement): void {
        this.modalElements.set(type, element);
    }

    public setAutoMoving(value: boolean): void {
        this.autoMoving.set(value);

        if (!value) {
            return;
        }

        window.setTimeout((): void => {
            this.autoMoving.set(false);
        }, 90);
    }

    public setModalsHidden(value: boolean): void {
        this.areModalsHidden.set(value);
    }

    public unregisterElement(type: MapModal): void {
        this.modalElements.delete(type);
    }

    private getOpenedModalTypes(): MapModal[] {
        return Object.values(MapModal).filter((type): boolean => {
            return type !== MapModal.NONE && this.state()[type].isOpen;
        });
    }
}
